import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { callApi } from '../../../utils/apiCall';
import newIdempotencyKey from '../../../utils/idempotencyKey';
import { parseApiDateTime } from '../../../utils/parseApiDateTime';
import { linkCall } from '../../../pages/dashboard/callLogs/caller/callerActions.api';
import { attachOutcome, formatDuration, SUGGESTIONS_URL, suggestionsOf } from './timelineCalls';

// Calls from this lead's number that weren't put on its timeline because another lead
// has the same number (a family landline). Staff pick: "This was them" (CU-869fmbgfq).
// Renders nothing when there are none, or the user can't view Call Logs (403/404).
export default function SharedNumberCalls({ practiceId, leadId, onAttached }) {
    const [state, setState] = useState({ key: null, rows: [] });
    const [busy, setBusy] = useState({});
    const [notes, setNotes] = useState({});
    const key = `${practiceId}:${leadId}`;
    const keyRef = useRef(key);
    keyRef.current = key;
    // One Idempotency-Key per intent (this call → this lead), reused if the user retries.
    const intentKeys = useRef({});

    const load = useCallback(async () => {
        if (!practiceId || !leadId) return;
        const forKey = `${practiceId}:${leadId}`;
        const response = await callApi(SUGGESTIONS_URL(practiceId, leadId), 'GET', null, false, { bustCache: true });
        if (keyRef.current !== forKey) return;
        setState({ key: forKey, rows: response.ok ? suggestionsOf(response.data) : [] });
    }, [practiceId, leadId]);

    useEffect(() => { setBusy({}); setNotes({}); intentKeys.current = {}; load(); }, [load]);

    const attach = async (row) => {
        const forKey = key;
        const intent = `${row.id}->${leadId}`;
        intentKeys.current[intent] = intentKeys.current[intent] || newIdempotencyKey();
        setBusy((b) => ({ ...b, [row.id]: true }));
        const result = await linkCall(
            practiceId,
            row,
            { leadId: Number(leadId), includeEarlier: false },
            intentKeys.current[intent],
        );
        if (keyRef.current !== forKey) return;
        const outcome = attachOutcome(result);
        // A reply that may have saved keeps the button locked until a refresh.
        setBusy((b) => ({ ...b, [row.id]: Boolean(outcome.locked) }));
        setNotes((n) => ({ ...n, [row.id]: outcome }));
        if (outcome.done) {
            setState((s) => ({ ...s, rows: s.rows.filter((r) => r.id !== row.id) }));
            if (onAttached) onAttached();
        }
    };

    const rows = state.key === key ? state.rows : [];
    if (!rows.length) return null;
    return (
        <Box sx={{ px: 2.5, pt: 1.5 }} data-testid="shared-number-calls">
            <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {rows.length === 1 ? '1 earlier call' : `${rows.length} earlier calls`} from this number may be this patient
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                    Another lead has the same number, so they weren&apos;t added automatically. Which of these was it?
                </Typography>
                <Stack spacing={0.75}>
                    {rows.map((row) => {
                        const when = parseApiDateTime(row.startedAt);
                        const note = notes[row.id];
                        const what = [
                            row.direction === 'outbound' ? 'Outgoing' : 'Incoming',
                            row.outcome === 'missed' ? 'missed' : row.outcome,
                            formatDuration(row.duration),
                            row.lineLabel && `Line: ${row.lineLabel}`,
                            row.number,
                        ].filter(Boolean).join(' · ');
                        return (
                            <Stack key={row.id} direction="row" alignItems="center" spacing={1} justifyContent="space-between" data-testid={`shared-call-${row.id}`}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{when ? when.format('DD MMM · h:mm a') : ''}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{what}</Typography>
                                    {note && !note.done && <Typography sx={{ fontSize: '0.7rem', color: note.tone === 'error' ? 'error.main' : 'text.secondary' }}>{note.message}</Typography>}
                                </Box>
                                <Button size="small" variant="outlined" disabled={Boolean(busy[row.id])} onClick={() => attach(row)} sx={{ flexShrink: 0, fontSize: '0.75rem' }}>
                                    This was them
                                </Button>
                            </Stack>
                        );
                    })}
                </Stack>
            </Alert>
        </Box>
    );
}

SharedNumberCalls.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onAttached: PropTypes.func,
};
